import { formatCostField, capitalizeSentenceFirstLetters } from '@repo/utils';
import { getOpenHoursText, getAddressText, getOpenHoursTextUniteUs, getAddressTextUniteUs } from './utils';
import React from 'react';
import { renderToString } from 'react-dom/server';
import { AbyssProvider } from '@uhg-abyss/web/ui/AbyssProvider';
import { createTheme } from '@uhg-abyss/web/tools/theme';
import { CboCardFindhelpNextSteps } from '@repo/ui';

function capitalize(word: string): string {
  return word ? word.charAt(0).toUpperCase() + word.slice(1) : '';
}

function section(label: string, value: string): string {
  return `
    <div class="section">
      <strong>${label}</strong>
      <div>${value || 'No information available'}</div>
    </div>`;
}

// The shared Next Steps component depends on Abyss theme context.
// For print, we SSR it with renderToString, so we must wrap it with
// AbyssProvider and pass a concrete theme to avoid missing-context errors
// and to keep print output aligned with in-app card rendering.
function findhelpNextStepsListHtml(programData: any, addressText: string): string {
  const office = programData?.offices?.[0];

  const rendered = renderToString(
    React.createElement(
      AbyssProvider,
      { theme: createTheme('uhc') },
      React.createElement(CboCardFindhelpNextSteps, {
        nextStepsData: programData?.next_steps ?? [],
        firstOfficeAddressText: addressText,
        firstOfficeEmail: office?.email ?? '',
        firstOfficePhone: office?.phone_number ?? '',
        firstOfficeWebsite: office?.website_url ?? '',
        // Avoid duplicate heading in print: section() already provides "Next Steps" label.
        source: 'member',
      })
    )
  );

  // Remove empty list items that can be produced by unsupported next-step channels.
  return rendered.replace(/<li[^>]*>\s*<\/li>/g, '');
}

const baseStyles = `
  body { font-family: Arial, sans-serif; font-size: 14px; color: #222; padding: 24px; max-width: 900px; margin: 0 auto; }
  h1 { font-size: 22px; margin-bottom: 4px; }
  .provider { color: #555; margin-bottom: 16px; }
  .description { margin-bottom: 20px; line-height: 1.5; }
  .section { margin-bottom: 14px; }
  .section strong { display: block; margin-bottom: 4px; }
  .section ul { list-style: none; padding-left: 0; margin: 0; }
  .section li { margin: 0 0 6px 0; }
  .two-col { display: flex; gap: 48px; }
  .two-col > div { flex: 1; }
  footer { margin-top: 32px; border-top: 1px solid #ccc; padding-top: 8px; font-size: 12px; color: #555; }
  hr { margin: 16px 0; }
`;

export function printFindhelp(programData: any): void {
  const cost = formatCostField(programData?.free_or_reduced);
  const addressText = getAddressText(programData);
  const office = programData?.offices?.[0];
  const nextStepsHtml = findhelpNextStepsListHtml(programData, addressText);

  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const hoursRows = days
    .map((day) => `<div>${capitalize(day)}: ${getOpenHoursText(office, day as any)}</div>`)
    .join('');

  const leftCol = [
    section('Main Services', programData.service_tags?.length
      ? programData.service_tags.map((t: string) => capitalizeSentenceFirstLetters(t)).join(', ')
      : 'No main services information available'),
    section('Serving', programData.attribute_tags?.length
      ? programData.attribute_tags.map((t: string) => capitalizeSentenceFirstLetters(t)).join(', ')
      : 'No serving information available'),
    section('Eligibility', programData.rules || 'No eligibility information is available'),
    section('Cost', cost),
    section('Languages', programData.supported_languages?.length
      ? programData.supported_languages.map((l: string) => capitalizeSentenceFirstLetters(l)).join(', ')
      : 'No language information is available'),
    section('Availability', programData.availability
      ? capitalize(programData.availability)
      : 'No availability information is available'),
    section('Coverage Area', programData.coverage_description || 'No coverage area information is available'),
  ].join('');

  const rightCol = [

    section('Location', addressText),
    section('Distance', `${programData?.distance ?? ''} miles from ${programData?.zipcode ?? ''}`),
    section('Next Steps', nextStepsHtml),
    section('Hours', hoursRows),
  ].join('');

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${programData.name}</title>
  <style>${baseStyles}</style>
</head>
<body>
  <h1>${programData.name}</h1>
  <div class="provider">by ${programData.provider_name || ''}</div>
  <div class="description">${programData.description || ''}</div>
  <hr />
  <div class="two-col">
    <div>${leftCol}</div>
    <div>${rightCol}</div>
  </div>
  <footer>This resource was shared by United Healthcare Community Resources, powered by Findhelp.</footer>
</body>
</html>`;

  const win = window.open('', '_blank');
  if (!win) return;
  win.document.write(html);
  win.document.close();
  win.focus();
  win.print();
}

export function printUniteUs(programData: any): void {
  const location = programData?.document?.locations?.[0];
  const closestLocation = programData?.document?.locations?.find((l: any) => l.is_closest) || location;
  const addressText = getAddressTextUniteUs(closestLocation);

  const langCodeShort = 'en';
  const displayNames = new Intl.DisplayNames([langCodeShort], { type: 'language' });

  const services = programData?.relationships?.services?.data ?? [];
  const servicesText = services.length
    ? services.map((s: any) => capitalizeSentenceFirstLetters(s.label || '')).join(', ')
    : 'No main services information available';

  const serving = programData?.document?.population_specializations ?? [];
  const servingText = serving.length
    ? serving.map((t: string) => capitalize(t.replace(/_/g, ' '))).join(', ')
    : 'No serving information available';

  const eligibility = programData?.document?.eligibility_text || 'No eligibility information is available';

  const payments = programData?.document?.payment_options ?? [];
  const costText = payments.length
    ? payments.map((p: string) => capitalize(p.replace(/_/g, ' '))).join(', ')
    : 'No pricing information is available';

  const languages = programData?.document?.languages ?? [];
  const languagesText = languages.length
    ? languages.map((l: string) => displayNames.of(l) || capitalize(l)).join(', ')
    : 'No language information is available';

  const availabilityText = programData?.receiving_referrals && programData?.active
    ? 'Available'
    : 'No availability information is available';

  const county = programData?.document?.locations?.[0]?.county;
  const coverageText = county
    ? county.split(' ').map((w: string) => capitalize(w)).join(' ')
    : 'No coverage area information is available';

  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const hoursRows = days
    .map((day) => `<div>${capitalize(day)}: ${getOpenHoursTextUniteUs(location?.hours, day)}</div>`)
    .join('');

  const distanceLine = programData?.document?.distance && location?.postal_code
    ? `<br>${String(programData.document.distance).replace('mi', '').trim()} miles from ${location.postal_code}`
    : '';

  const popRestrictions = programData?.document?.population_restrictions ?? [];
  const popText = popRestrictions.length
    ? popRestrictions.map((r: string) => capitalize(r.replace(/_/g, ' '))).join(', ')
    : 'No population restrictions';

  const restrictedCities = programData?.document?.restricted_cities ?? [];
  const citiesText = restrictedCities.length
    ? restrictedCities.map((c: string) => capitalize(c.replace(/_/g, ' '))).join(', ')
    : 'No restricted cities';

  const restrictedCounties = programData?.document?.restricted_counties ?? [];
  const countiesText = restrictedCounties.length
    ? restrictedCounties.map((c: string) => capitalize(c.replace(/_/g, ' '))).join(', ')
    : 'No restricted counties';

  const restrictedStates = programData?.document?.restricted_states ?? [];
  const statesText = restrictedStates.length
    ? restrictedStates.map((s: string) => capitalize(s.replace(/_/g, ' '))).join(', ')
    : 'No restricted states';

  const leftCol = [
    section('Main Services', servicesText),
    section('Serving', servingText),
    section('Eligibility', eligibility),
    section('Cost', costText),
    section('Languages', languagesText),
    section('Availability', availabilityText),
    section('Coverage Area', coverageText),
  ].join('');

  const rightCol = [
    section('Location', `${addressText}${distanceLine}`),
    section('Population Restrictions', popText),
    section('Restricted Cities', citiesText),
    section('Restricted Counties', countiesText),
    section('Restricted States', statesText),
    section('Hours', hoursRows),
  ].join('');

  const providerName = programData?.relationships?.provider?.data?.document?.name || '';

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${programData?.document?.name}</title>
  <style>${baseStyles}</style>
</head>
<body>
  <h1>${programData?.document?.name}</h1>
  ${providerName ? `<div class="provider">by ${providerName}</div>` : ''}
  <div class="description">${programData?.document?.description || ''}</div>
  <hr />
  <div class="two-col">
    <div>${leftCol}</div>
    <div>${rightCol}</div>
  </div>
  <footer>This resource was shared by United Healthcare Community Resources, powered by Unite Us.</footer>
</body>
</html>`;

  const win = window.open('', '_blank');
  if (!win) return;
  win.document.write(html);
  win.document.close();
  win.focus();
  win.print();
}
